import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Invoice, OverrideStatus } from '../../invoice.model';
import { CalculatedFields } from './invoice-details.component';
import { InvoiceService } from '../../invoice.service';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CurrencyMaskDirective } from '../../../../UI-Lib/Currency/Mask/currencyMask.directive';

@Component({
    selector: 'tr[invoiceDetailsTotals]',
    templateUrl: 'invoice-details-totals.component.html',
    imports: [CommonModule, FormsModule, CurrencyMaskDirective],
})
export class InvoiceDetailsTotalsComponent {
    constructor(
        private invoiceService: InvoiceService,
        private numberPipe: DecimalPipe,
    ) {}

    @Input() editMode: boolean = false;
    @Input() currentInvoice: Invoice;
    @Output() calculatedFieldChanged: EventEmitter<CalculatedFields> = new EventEmitter<CalculatedFields>();

    CalculatedFields = CalculatedFields;

    revertPreviousFMV(shouldCalcInvoiceData: boolean): void {
        if (
            this.currentInvoice.previousFMVStatus === OverrideStatus.Overridden &&
            this.currentInvoice.invoiceFMVStatus === OverrideStatus.Default
        ) {
            if (
                this.currentInvoice.taxRateStatus === OverrideStatus.Recalculated &&
                this.currentInvoice.savingsStatus === OverrideStatus.Recalculated
            ) {
                // The basis for the tax rate/savings calculations changes when we switch from overriden FMV change
                // back to default; if the tax rate and savings are already both in a recalculated state, we want
                // to restore to a "pristine" state, so restore the savings here
                this.currentInvoice = this.invoiceService.resetTotalSavings(this.currentInvoice);
            }
        }

        this.currentInvoice.previousFMV = this.currentInvoice.calculatedPreviousFMV;

        if (shouldCalcInvoiceData) {
            this.currentInvoice = this.invoiceService.calculateInvoiceData(this.currentInvoice);
        }
    }

    revertInvoiceFMV(shouldCalcInvoiceData: boolean): void {
        if (
            this.currentInvoice.invoiceFMVStatus === OverrideStatus.Overridden &&
            this.currentInvoice.previousFMVStatus === OverrideStatus.Default
        ) {
            if (
                this.currentInvoice.taxRateStatus === OverrideStatus.Recalculated &&
                this.currentInvoice.savingsStatus === OverrideStatus.Recalculated
            ) {
                // (same as above)
                this.currentInvoice = this.invoiceService.resetTotalSavings(this.currentInvoice);
            }
        }

        this.currentInvoice.invoiceFMV = this.currentInvoice.calculatedInvoiceFMV;

        if (shouldCalcInvoiceData) {
            this.currentInvoice = this.invoiceService.calculateInvoiceData(this.currentInvoice);
        }
    }

    revertTaxRate(shouldCalcInvoiceData: boolean): void {
        this.currentInvoice.taxRate = parseFloat(
            this.numberPipe.transform(this.currentInvoice.calculatedTaxRate, '1.8-8'),
        );
        this.currentInvoice.displayTaxRate = this.invoiceService.getDisplayPercent(this.currentInvoice.taxRate);
        if (
            this.currentInvoice.savingsStatus === OverrideStatus.Default ||
            this.currentInvoice.savingsStatus === OverrideStatus.Recalculated
        ) {
            this.currentInvoice = this.invoiceService.resetTotalSavings(this.currentInvoice);
        }

        if (shouldCalcInvoiceData) {
            this.currentInvoice = this.invoiceService.calculateInvoiceData(this.currentInvoice);
        }
    }

    revertSavings(): void {
        this.currentInvoice.isSavingsOverridden = false;
        this.currentInvoice.savingsStatus = OverrideStatus.Default;
        this.currentInvoice.savings = this.currentInvoice.calculatedSavings;
        if (
            this.currentInvoice.taxRateStatus === OverrideStatus.Recalculated ||
            this.currentInvoice.taxRateStatus === OverrideStatus.Default
        ) {
            if (this.currentInvoice.fmvChangeStatus === OverrideStatus.Recalculated) {
                this.currentInvoice.taxRate = this.invoiceService.calculateInitialTaxRate(this.currentInvoice);
            } else {
                this.currentInvoice.taxRate = parseFloat(
                    this.numberPipe.transform(this.currentInvoice.calculatedTaxRate, '1.8-8'),
                );
            }
        }

        if (
            this.currentInvoice.contingencyPctStatus === OverrideStatus.Default &&
            this.currentInvoice.contingencyCapStatus === OverrideStatus.Default &&
            this.currentInvoice.savingsStatus === OverrideStatus.Default &&
            this.currentInvoice.feeAmountStatus !== OverrideStatus.Overridden
        ) {
            this.currentInvoice.feeAmount = this.invoiceService.calculateFeeAmount(this.currentInvoice);
        }

        this.currentInvoice = this.invoiceService.calculateInvoiceData(this.currentInvoice);
    }
}
